import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksElement } from '../BlocksElement';
import type { DropdownPanelSize, DropdownPanelVariant, DropdownPlacement } from './dropdown-panel.model';
import styles from './dropdown-panel.styles';

/**
 * @slot header - The component header content
 * @slot default - The component content body
 * @slot footer - The component footer content
 * @csspart base - The component's base wrapper.
 * @csspart header-wrapper - The component's header wrapper.
 * @csspart part-header - The component's header.
 * @csspart body - The component's body wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart panel-footer - The component's footer slot.
 */
export class BlocksDropdownPanel extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Adds a max height and scrolling of the panel
   */
  @property({ type: Boolean })
  hasScroll: boolean | undefined;

  /**
   * Changes styling on panel for variant with header
   */
  @property({ type: Boolean })
  hasHeader = false;

  /**
   * Changes styling on panel for variant with footer
   */
  @property({ type: Boolean })
  hasFooter = false;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**bare** removes border from dropdown panel</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: DropdownPanelVariant;

  @property({ type: String })
  placement: DropdownPlacement;

  /**
   * Property to track the list item empty status. This will be used to hide the empty panel when the list item is empty.
   */
  @property({ type: Boolean })
  isEmpty = false;

  /**
   * Size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** increases the dropdown height with label inside</li>
   * <li>**md** reduce the dropdown height with label outside </li>
   * <li>**sm** reduce the dropdown height by removing label </li>
   * </ul>
   * </cat-text-passage>
   * The padding values (through properties) are set in dropdown.scss file for the dropdown panel component and now this is moving to overlay (to the body) then the corresponding CSS will not be available in panel component and currently the padding CSS moves to dropdown panel and enabling the CSS based on the size condition in panel component.
   */

  @property({ type: String })
  size: DropdownPanelSize = 'lg';

  render(): TemplateResult<1> {
    if (this.isEmpty) {
      return html`${nothing}`;
    }
    const componentClassNames = this.componentClassNames('cat-c-dropdown-panel cat-c-dropdown--large', {
      'cat-c-dropdown-panel--bare': this.variant === 'bare',
      'cat-has-header': this.hasHeader === true,
      'cat-has-footer': this.hasFooter === true,
      'cat-has-scroll': this.hasScroll === true,
      'cat-c-dropdown--large': this.size === 'lg',
      'cat-c-dropdown--medium': this.size === 'md',
      'cat-c-dropdown--small': this.size === 'sm'
    });

    return html`
      <div part="base" class="${componentClassNames}">
        ${this.slotNotEmpty('header')
          ? html`
              <div part="header-wrapper" class="cat-c-dropdown-panel__header">
                <slot part="part-header" name="header"></slot>
              </div>
            `
          : html`${nothing}`}
        <div part="body" class="cat-c-dropdown-panel__body">
          <slot part="part-default"></slot>
        </div>
        ${this.slotNotEmpty('footer')
          ? html`
              <div part="footer-wrapper" class="cat-c-dropdown-panel__footer">
                <slot part="panel-footer" name="footer"></slot>
              </div>
            `
          : html`${nothing}`}
      </div>
    `;
  }
}

if (customElements.get('cat-dropdown-panel') === undefined) {
  customElements.define('cat-dropdown-panel', BlocksDropdownPanel);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-dropdown-panel': BlocksDropdownPanel;
  }
}
